'use client';

import { useEffect } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button/button';
import { capturePostHogException } from '@/lib/posthog-client-errors';

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Global Error Boundary
 *
 * Catches unhandled errors in the app and displays a user-friendly message.
 * Provides options to retry or navigate to a safe page.
 */
export default function GlobalError({ error, reset }: ErrorPageProps) {
  useEffect(() => {
    capturePostHogException(error, { boundary: 'app/error' });
    console.error('[GlobalError]', error);
  }, [error]);

  const isNetworkError =
    error.message.includes('fetch') ||
    error.message.includes('network') ||
    error.message.includes('ECONNREFUSED');

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-4 dark:bg-zinc-950">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
          <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
            {isNetworkError ? 'Connection Error' : 'Something went wrong'}
          </h1>
          <p className="text-zinc-600 dark:text-zinc-400">
            {isNetworkError
              ? 'Unable to connect to the server. Please check your connection and try again.'
              : 'An unexpected error occurred. Our team has been notified.'}
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button onClick={reset} variant="default" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
          <Button onClick={() => (window.location.href = '/login')} variant="outline">
            Go to Login
          </Button>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <details className="mt-6 rounded-lg border border-zinc-200 bg-zinc-100 p-4 text-left dark:border-zinc-800 dark:bg-zinc-900">
            <summary className="cursor-pointer text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Error Details (Development Only)
            </summary>
            <pre className="mt-2 overflow-auto text-xs text-zinc-600 dark:text-zinc-400">
              {error.message}
              {error.stack && `\n\n${error.stack}`}
              {error.digest && `\n\nDigest: ${error.digest}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
